// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandAlphabetLetterModel, BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { VerticalColumnCellProps } from "@insite/content-library/Components/VerticalColumnCell";
import { VerticalColumnContainerProps } from "@insite/content-library/Components/VerticalColumnContainer";
import { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@steiner/App/Components/Notify";
import BrandAlphabetNavigation, {
    BrandAlphabetNavigationStyles,
} from "@steiner/Overrides/Widgets/Brand/BrandAlphabetNavigation";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    allBrandsDataView: getAllBrandsDataView(state),
    brandAlphabetState: state.pages.brands.brandAlphabetState,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

type BrandLetterMap = {
    [letter: string]: BrandModel[];
};

export interface BrandListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: InjectableCss;
    brandAlphabetNavigation?: BrandAlphabetNavigationStyles;
    brandAccordion?: AccordionProps;
    brandAccordionSection?: AccordionSectionPresentationProps;
    verticalColumnContainer?: VerticalColumnContainerProps;
    verticalColumnCell?: VerticalColumnCellProps;
    brandLink?: LinkPresentationProps;
    actions?: InjectableCss;
    actionLinks?: LinkPresentationProps;
    actionLinksDisabled?: TypographyPresentationProps;
    buttons?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
    // XNG-Changes
    titleWrapper?: InjectableCss;
    brandSections?: InjectableCss;
    brandAlphabetSections?: InjectableCss;
    allBrandsTitle?: TypographyPresentationProps;
    clearFiltersButton?: ButtonPresentationProps;
    brandLetter?: TypographyPresentationProps;
    backToTopLink?: LinkPresentationProps;
    brandItem?: InjectableCss;
    brandItems?: InjectableCss;
    searchKeyTitleWrapper?: InjectableCss;
}

export const listStyles: BrandListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    brandAccordionSection: {
        titleTypographyProps: {
            weight: "bold",
            css: css`
                text-transform: uppercase;
            `,
        },
    },
    backToTopButton: {
        variant: "secondary",
        sizeVariant: "small",
        css: css`
            margin: 10px;
        `,
    },
    brandLink: {
        css: css`
            width: 100%;
            display: flex;
            &:focus {
                outline-offset: -2px;
            }
        `,
    },
    verticalColumnContainer: {
        columnCounts: [1, 2, 2, 4, 4],
    },
    actions: {
        css: css`
            display: flex;
            justify-content: flex-end;
            padding: 5px;
        `,
    },
    actionLinks: {
        css: css`
            margin: 5px;
        `,
    },
    actionLinksDisabled: {
        css: css`
            margin: 5px;
        `,
        color: "text.disabled",
    },
    buttons: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
};

const styles = listStyles;

interface BrandListState {
    expanded: boolean | "mixed";
    brandLetterDetails: BrandAlphabetLetterModel[];
    brandSections: BrandSection[];
    // XNG-Changes
    selectedLetter: string;
}

/**
 * This will display all Brands grouped by the first character.
 * Starting with numbers grouped into '#'.
 * @param props
 */
const BrandList: FC<Props> = (props: Props) => {
    const { allBrandsDataView, brandAlphabetState } = props;

    const brandList = allBrandsDataView.value || [];
    const brandAlphabet = brandAlphabetState.value || [];
    const { letters, brandLettersMap } = buildLettersAndMap(brandList);
    // XNG-Changes
    const [searchFilterBrands, setSearchFilterBrands] = React.useState("");
    const { brandLetterDetails, brandSections } = buildBrandSections(
        letters,
        brandLettersMap,
        brandAlphabet,
        false,
        searchFilterBrands,
    );

    const [state, setState] = React.useState<BrandListState>({
        expanded: false,
        brandLetterDetails,
        brandSections,
        selectedLetter: "",
    });

    // XNG-Changes
    React.useEffect(() => {
        setState({ ...state, brandLetterDetails, brandSections });
    }, [brandLetterDetails?.length, brandSections?.length, searchFilterBrands]);

    if (allBrandsDataView.isLoading || brandAlphabetState.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }
    if (brandList.length === 0) {
        return null;
    }

    const onClickBrandSection = (section: BrandSection) => () => {
        setState(({ brandSections, expanded, brandLetterDetails }: BrandListState) => {
            const itemIndex = brandSections.findIndex(item => item.letter === section.letter);
            const nextSectionExpanded = !section.expanded;
            let nextExpanded = expanded;
            if (expanded !== "mixed" && expanded !== nextSectionExpanded) {
                nextExpanded = "mixed";
            } else if (expanded === "mixed") {
                const hasNonMatchingExpanded = brandSections.find(item => {
                    return item.letter !== section.letter && item.expanded !== nextSectionExpanded;
                });
                if (!hasNonMatchingExpanded) {
                    nextExpanded = nextSectionExpanded;
                }
            }
            const newSectionState = { ...section, expanded: nextSectionExpanded };
            const newBrandSections = [...brandSections];
            newBrandSections.splice(itemIndex, 1, newSectionState);
            return {
                brandSections: newBrandSections,
                expanded: nextExpanded,
                brandLetterDetails,
                selectedLetter: section.letter,
            };
        });
    };

    const handleBackToTop = () => {
        const element = document.querySelector(`[data-test-selector="brandList"]`) as HTMLElement;
        const offset = element?.offsetTop ?? 0;
        window.scrollTo({ top: Math.max(offset - 44, 0), behavior: "smooth" });
        const elementToFocus = document.getElementById("collapseButton");
        if (elementToFocus) {
            elementToFocus.focus();
        } else {
            document.getElementById("expandButton")?.focus();
        }
    };

    // XNG-Changes
    const clearFilters = () => {
        setSearchFilterBrands("");
        setState(prevState => ({ ...prevState, selectedLetter: "" }));
    };

    const handleBrandLetterClick = (letter: string) => {
        if (letter === "all") {
            clearFilters();
        }
        const section = state.brandSections.find(item => item.letter === letter);
        section && onClickBrandSection(section)();
    };

    // XNG-Changes: adding filter's function
    const handleSearchFilterBrands = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value: searchFilterTextValue } = e.target;
        setSearchFilterBrands(searchFilterTextValue);
        const { letters, brandLettersMap } = buildLettersAndMap(brandList);

        const brandSections = buildBrandSections(letters, brandLettersMap, brandAlphabet, false, searchFilterTextValue);

        setState({
            ...state,
            brandLetterDetails: brandSections.brandLetterDetails,
            brandSections: brandSections.brandSections,
        });
    };

    // XNG-Changes
    const getTitle = () => {
        let title = "";
        if (state.selectedLetter) {
            title = translate(
                `Filtered Results for: "${state.selectedLetter === "#" ? "0-9" : state.selectedLetter?.toUpperCase()}"`,
            );
        }
        if (searchFilterBrands) {
            title = translate(`Filtered Results for: "${searchFilterBrands}"`);
        }
        return title;
    };

    const brandSectionsState = state.selectedLetter
        ? state.brandSections.filter(brand => brand.letter === state.selectedLetter)
        : state.brandSections;

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandList">
            {/* XNG-Changes */}
            <Typography variant="h2">{translate("All Brands")}</Typography>
            <BrandAlphabetNavigation
                letterDetails={brandLetterDetails}
                extendedStyles={styles.brandAlphabetNavigation}
                onBrandLetterClick={handleBrandLetterClick}
                handleSearchFilterBrands={handleSearchFilterBrands}
                searchFilterBrands={searchFilterBrands}
                selectedLetter={state.selectedLetter || "all"}
            />
            {(state.selectedLetter || searchFilterBrands) && (
                <StyledWrapper {...styles.titleWrapper}>
                    <StyledWrapper {...styles.searchKeyTitleWrapper}>
                        <Typography as="h2" {...styles.allBrandsTitle}>
                            {getTitle()}
                        </Typography>
                    </StyledWrapper>
                    {(state.selectedLetter || searchFilterBrands) && (
                        <Button
                            {...styles.clearFiltersButton}
                            aria-label={translate("Clear Filters")}
                            onClick={clearFilters}
                        >
                            {translate("Clear Filters")}
                        </Button>
                    )}
                </StyledWrapper>
            )}
            {brandSectionsState.length > 0 &&
                brandSectionsState.filter(brandDetails => brandDetails.brandMap.length > 0).length === 0 && (
                    <Notify type="info">{translate("No results found")}</Notify>
                )}
            {brandSectionsState
                .filter(brandDetails => brandDetails.brandMap.length > 0)
                .map(brandDetails => (
                    <StyledWrapper key={brandDetails.letter} {...styles.brandSections}>
                        <StyledWrapper {...styles.brandAlphabetSections}>
                            <Typography
                                id={`letter-${brandDetails.letter === "#" ? "number" : brandDetails.letter}`}
                                {...styles.brandLetter}
                            >
                                {brandDetails.letter === "#" ? "0-9" : brandDetails.letter}
                            </Typography>
                            <Link {...styles.backToTopLink} onClick={handleBackToTop}>
                                {translate("Back to Top")}
                            </Link>
                        </StyledWrapper>
                        <StyledWrapper {...styles.brandItems}>
                            {brandDetails.brandMap.map(brand => (
                                <StyledWrapper key={brand.detailPagePath} {...styles.brandItem}>
                                    <Link
                                        {...styles.brandLink}
                                        href={brand.detailPagePath}
                                        data-test-selector="brandLink"
                                    >
                                        {brand.name}
                                    </Link>
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BrandList),
    definition: {
        group: "Brands",
        icon: "List",
        allowedContexts: ["BrandsPage"],
    },
};

export default widgetModule;

interface BrandSection {
    letter: string;
    brandMap: BrandModel[];
    expanded: boolean;
}

/**
 * Takes in a brand list and sorts it into #, A-Z, Other characters.
 *
 * @param brandList The list of brands that should be sorted.
 */
const buildLettersAndMap = (brandList: BrandModel[]): { letters: string[]; brandLettersMap: BrandLetterMap } => {
    let letters: string[] = [];
    let letter: string;
    let newLetters: string[] = letters;
    const brandLettersMap: BrandLetterMap = {};
    for (let i = 0; i < brandList.length; i = i + 1) {
        const brand = brandList[i];
        letter = brand.name[0] ? brand.name[0].toLowerCase() : "";
        if (isNumeric(letter)) {
            letter = "#";
        }
        if (!brandLettersMap[letter]) {
            brandLettersMap[letter] = [];
        }
        brandLettersMap[letter].push(brand);
        newLetters = [...newLetters, letter];
    }
    letters = newLetters.filter((letter, index, array) => array.indexOf(letter) === index);
    if (brandLettersMap["#"]) {
        brandLettersMap["#"].sort((a, b) => parseInt(a.name, 10) - parseInt(b.name, 10));
    }

    return { letters, brandLettersMap };
};

/**
 * Take in the props and create easier to work with state objects.
 *
 * @param letters List of letters and special characters.
 * @param brandLettersMap Brand list sorted into letter lists.
 * @param brandAlphabet Letter count list.
 * @param expanded The expanded value to use if not undefined.
 * XNG-change
 * @param searchFilterBrands
 */
const buildBrandSections = (
    letters: string[],
    brandLettersMap: BrandLetterMap,
    brandAlphabet: BrandAlphabetLetterModel[],
    expanded: boolean | undefined,
    searchFilterBrands?: string,
): {
    brandLetterDetails: {
        count: number;
        letter: string;
        linkable: boolean;
    }[];
    brandSections: {
        letter: string;
        brandMap: BrandModel[];
        expanded: boolean;
    }[];
} => {
    const brandLetterDetails = brandAlphabet.map(alphabetItem => ({
        ...alphabetItem,
        linkable: alphabetItem.count > 0,
    }));

    // XNG-Changes
    const brandSections = letters.map(letter => ({
        letter,
        brandMap: getBrandLettersMap(brandLettersMap, letter, searchFilterBrands),
        expanded: expanded || false,
    }));

    return { brandLetterDetails, brandSections };
};

// XNG change
const getBrandLettersMap = (
    brandLettersMap: BrandLetterMap,
    letter: string,
    searchFilterBrands?: string | undefined,
) => {
    if (!searchFilterBrands) {
        return brandLettersMap[letter];
    }

    if (searchFilterBrands) {
        brandLettersMap[letter] = brandLettersMap[letter].filter(brand =>
            (brand.name || "").toLowerCase().includes(searchFilterBrands.toLowerCase()),
        );
    }

    return brandLettersMap[letter];
};
